import { isEmptyImportValue } from './isEmptyImportValue'
import type { ImportApplyWarning, ImportDefault, ImportDefaultsMaps } from './types'

function refMapKey(attr: string, lookupField: string): string {
  return `${attr}|${lookupField}`
}

export function applyImportDefaultsToRow(
  row: Record<string, unknown>,
  defaults: ImportDefault[],
  maps: ImportDefaultsMaps,
  rowNum: number,
  opts?: { compositeTypeSuffix?: string },
): { row: Record<string, unknown>; warnings: ImportApplyWarning[] } {
  const typeSuffix = opts?.compositeTypeSuffix ?? '_type'
  let nextRow = { ...row }
  const warnings: ImportApplyWarning[] = []

  for (const def of defaults) {
    switch (def.kind) {
      case 'scalar': {
        if (!isEmptyImportValue(nextRow[def.attr])) break
        nextRow = { ...nextRow, [def.attr]: def.value }
        break
      }
      case 'enum': {
        if (!isEmptyImportValue(nextRow[def.attr])) break
        const resolveMap = maps.enumMaps[def.attr] ?? {}
        const hit = resolveMap[def.value]
        if (hit) {
          if (hit.status === 'missing') {
            nextRow = { ...nextRow, [def.attr]: '' }
            warnings.push({
              row: rowNum,
              attr: def.attr,
              message: `Missing enum match for default "${def.value}" on ${def.attr}`,
            })
          } else {
            nextRow = { ...nextRow, [def.attr]: hit.name ?? def.value }
            if (hit.status === 'ambiguous') {
              warnings.push({
                row: rowNum,
                attr: def.attr,
                message: `Ambiguous enum match for default "${def.value}" on ${def.attr}`,
              })
            }
          }
        } else {
          nextRow = { ...nextRow, [def.attr]: def.value }
        }
        break
      }
      case 'ref': {
        if (!isEmptyImportValue(nextRow[def.attr])) break
        const mapKey = refMapKey(def.attr, def.lookupField)
        const resolveMap = maps.refMaps[mapKey] ?? {}
        const hit = resolveMap[def.text]
        if (!hit || hit.status === 'missing') {
          nextRow = { ...nextRow, [def.attr]: '' }
          warnings.push({
            row: rowNum,
            attr: def.attr,
            message: `Missing ref match for default "${def.text}" on ${def.attr}`,
          })
        } else {
          nextRow = { ...nextRow, [def.attr]: hit.id ?? '' }
          if (hit.status === 'ambiguous') {
            warnings.push({
              row: rowNum,
              attr: def.attr,
              message: `Ambiguous ref match for default "${def.text}" on ${def.attr}`,
            })
          }
        }
        break
      }
      case 'composite': {
        const typeKey = `${def.attr}${typeSuffix}`
        const valueEmpty = isEmptyImportValue(nextRow[def.attr])
        const typeEmpty = isEmptyImportValue(nextRow[typeKey])
        if (!valueEmpty || !typeEmpty) break

        if (!def.type || def.type.trim() === '') {
          warnings.push({
            row: rowNum,
            attr: def.attr,
            message: `Invalid composite default type on ${def.attr}`,
          })
          break
        }
        nextRow = { ...nextRow, [def.attr]: def.value, [typeKey]: def.type }
        break
      }
      default: {
        const _exhaustive: never = def
        void _exhaustive
      }
    }
  }

  return { row: nextRow, warnings }
}
